import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { LayoutDashboard, Truck, Users, FileText, Wallet, UserCog, Settings, Building2, LogOut, Menu, ArrowLeftRight, Contact, Gauge, ClipboardCheck, Handshake, Store, Route, BarChart3, ChevronDown, BookOpen, Receipt, ShoppingCart, Landmark, Percent, IdCard, CalendarCheck, HandCoins, FileSpreadsheet, PiggyBank, Radar, Fuel, Clock } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/context/ThemeContext";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "manager", "accounts"] },
  {
    to: "/booking-group", label: "Bookings & LR", icon: FileText, roles: ["admin", "manager", "operation", "accounts"],
    children: [
      { to: "/party-bookings", label: "Party Bookings", icon: BookOpen, roles: ["admin", "manager", "operation", "accounts"] },
      { to: "/bookings", label: "Manual LR / Bilty", icon: FileText, roles: ["admin", "manager", "operation", "accounts"] },
      { to: "/bulk-lr", label: "Bulk LR & Booking Upload", icon: FileSpreadsheet, roles: ["admin", "manager", "operation"] },
      { to: "/pod-records", label: "POD Records", icon: ClipboardCheck, roles: ["admin", "manager", "accounts"] },
    ],
  },
  {
    to: "/market", label: "Market Vehicle & Vendors", hint: "मार्केट गाड़ी और वेंडर प्रबंधन", icon: Handshake, roles: ["admin", "manager", "operation", "accounts"],
    children: [
      { to: "/market/trips", label: "Market Trips", icon: Route, roles: ["admin", "manager", "operation", "accounts"] },
      { to: "/market/reports", label: "Reports", icon: BarChart3, roles: ["admin", "manager", "accounts"] },
    ],
  },
  { to: "/tracking", label: "Live Fleet Tracking & Status", icon: Radar, roles: ["admin", "manager", "operation", "accounts"] },
  {
    to: "/fleet-group", label: "Fleet & Driver", icon: Truck, roles: ["admin", "manager", "operation", "accounts"],
    children: [
      { to: "/vehicles", label: "Fleet", icon: Truck, roles: ["admin", "manager", "operation"] },
      { to: "/drivers", label: "Drivers", icon: Users, roles: ["admin", "manager", "operation", "accounts"] },
      { to: "/mileage", label: "Mileage", icon: Gauge, roles: ["admin", "manager"] },
      { to: "/per-km-report", label: "Per-KM Trip Report", icon: Gauge, roles: ["admin", "manager", "accounts"] },
      { to: "/idle-days", label: "Idle Days Report", icon: Clock, roles: ["admin", "manager", "operation", "accounts"] },
      { to: "/driver-app", label: "Driver App & Tracking", icon: Radar, roles: ["admin", "manager", "operation"] },
    ],
  },
  {
    to: "/masters-group", label: "Masters", icon: Contact, roles: ["admin", "manager", "operation", "accounts"],
    children: [
      { to: "/parties", label: "Parties", icon: Contact, roles: ["admin", "manager", "operation", "accounts"] },
      { to: "/market/vendors", label: "Vendors", icon: Store, roles: ["admin", "manager", "operation", "accounts"] },
      { to: "/suppliers", label: "Suppliers", icon: ShoppingCart, roles: ["admin", "accounts"] },
    ],
  },
  {
    to: "/profit-loss", label: "Profit & Loss", icon: BarChart3, roles: ["admin", "accounts"],
    children: [
      { to: "/profit-loss/summary", label: "Portfolio Summary", icon: BarChart3, roles: ["admin", "accounts"] },
      { to: "/profit-loss/turnover", label: "Monthly Turnover", icon: Receipt, roles: ["admin", "accounts"] },
      { to: "/profit-loss/expense", label: "Monthly Expense", icon: Wallet, roles: ["admin", "accounts"] },
      { to: "/profit-loss/own", label: "Own Vehicle Profit", icon: Truck, roles: ["admin", "accounts"] },
      { to: "/profit-loss/market", label: "Market Vehicle Profit", icon: Store, roles: ["admin", "accounts"] },
    ],
  },
  {
    to: "/finance-group", label: "Finance & Central Expenses", hint: "फाइनेंस और कुल खर्च", icon: Wallet, roles: ["admin", "manager", "accounts"],
    children: [
      { to: "/central-expenses", label: "Central Expenses", icon: Receipt, roles: ["admin", "accounts"] },
      { to: "/purchases", label: "Purchases & Bills", icon: ShoppingCart, roles: ["admin", "accounts"] },
      { to: "/tds", label: "TDS Management", icon: Percent, roles: ["admin", "accounts"] },
      { to: "/bank", label: "Bank & Reconciliation", icon: Landmark, roles: ["admin", "accounts"] },
      { to: "/loans", label: "Loans & Liabilities", hint: "लोन और उधारी प्रबंधन", icon: PiggyBank, roles: ["admin", "accounts"] },
      { to: "/gst", label: "GST Returns", icon: Percent, roles: ["admin", "accounts"] },
      { to: "/gst-ledger", label: "GST Ledger & Tax Management", icon: Percent, roles: ["admin", "accounts"] },
      { to: "/finance", label: "Ledger, P&L & Approvals", icon: Wallet, roles: ["admin", "manager", "accounts"] },
    ],
  },
  {
    to: "/fuel-group", label: "Fuel & Tolls", hint: "डीज़ल और टोल", icon: Fuel, roles: ["admin", "manager", "accounts", "operation"],
    children: [
      { to: "/diesel", label: "Diesel Inbox & Assign", icon: Fuel, roles: ["admin", "manager", "accounts", "operation"] },
      { to: "/fastag", label: "FASTag Toll Tracker", icon: Radar, roles: ["admin", "manager", "accounts"] },
    ],
  },
  {
    to: "/hr-group", label: "Staff & HR Management", hint: "स्टाफ और सैलरी प्रबंधन", icon: IdCard, roles: ["admin", "manager", "accounts"],
    children: [
      { to: "/hr/staff", label: "Staff Master", icon: Users, roles: ["admin", "manager", "accounts"] },
      { to: "/hr/attendance", label: "Attendance & Leave", icon: CalendarCheck, roles: ["admin", "manager", "accounts"] },
      { to: "/hr/advances", label: "Advance & Bonus", icon: HandCoins, roles: ["admin", "manager", "accounts"] },
      { to: "/hr/payroll", label: "Payroll & Pay Slips", icon: FileSpreadsheet, roles: ["admin", "manager", "accounts"] },
    ],
  },
  { to: "/users", label: "Team & Access", icon: UserCog, roles: ["admin"] },
  { to: "/settings", label: "Company", icon: Settings, roles: ["admin"] },
  { to: "/platform", label: "Companies", icon: Building2, roles: ["super_admin"] },
];

function CompanySwitcher() {
  const { user, isSuper, actAs, setActAs, setActiveCompany, role } = useAuth();
  const navigate = useNavigate();
  const [newOpen, setNewOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const companies = user?.companies || [];
  const canCreate = !isSuper && role === "admin";
  if (!isSuper && companies.length < 2 && !canCreate) return null;
  const current = isSuper ? actAs?.id || "" : user?.company_id || "";
  const pick = async (id) => {
    if (id === "__new__") return setNewOpen(true);
    if (isSuper) {
      const c = companies.find((x) => x.id === id);
      setActAs(c ? { id: c.id, name: c.name } : null);
      navigate(c ? "/dashboard" : "/platform");
      return;
    }
    await setActiveCompany(id);
    navigate("/");
  };
  const create = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/companies", { name });
      toast.success(`${data.name} created`);
      setNewOpen(false);
      setName("");
      await setActiveCompany(data.id);
      navigate("/");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <>
      <select data-testid="company-switcher" className="native-select h-9 w-auto max-w-[260px] text-sm font-semibold" value={current} onChange={(e) => pick(e.target.value)}>
        {isSuper && <option value="">Platform Console</option>}
        {companies.map((c) => <option key={c.id} value={c.id}>{c.name}{!isSuper && c.role ? ` · ${c.role}` : ""}</option>)}
        {canCreate && <option value="__new__">+ New Company</option>}
      </select>
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="new-company-dialog">
          <DialogHeader>
            <DialogTitle>New Company</DialogTitle>
            <DialogDescription>Ek alag workspace banega — uska data (fleet, drivers, bookings, finance) baaki companies se poori tarah alag rahega. Aap iske Admin honge.</DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4">
            <input data-testid="new-company-name-input" autoFocus required placeholder="Company name" value={name} onChange={(e) => setName(e.target.value)} className="native-select w-full" />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setNewOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="new-company-save-btn" disabled={busy || !name.trim()}>Create &amp; Switch</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`;

function NavGroup({ n, role, onNavigate }) {
  const { pathname } = useLocation();
  const active = n.children.some((c) => pathname.startsWith(c.to));
  const [open, setOpen] = useState(active);
  return (
    <div>
      <button type="button" data-testid={`nav-group-${n.to.slice(1)}`} onClick={() => setOpen(!open)}
        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors hover:bg-secondary hover:text-foreground ${active ? "text-foreground" : "text-muted-foreground"}`}>
        <n.icon className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1"><span className="block leading-tight">{n.label}</span>{n.hint && <span className="block text-[10px] font-normal text-muted-foreground">{n.hint}</span>}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="ml-4 mt-1 space-y-1 border-l pl-2">
          {n.children.filter((c) => c.roles.includes(role)).map((c) => (
            <NavLink key={c.to} to={c.to} onClick={onNavigate} data-testid={`nav-${c.to.slice(1).replace("/", "-")}`} className={linkCls}>
              <c.icon className="h-4 w-4" />{c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function SideNav({ onNavigate }) {
  const { role, user, companyName, logout } = useAuth();
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 px-5 py-5">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Truck className="h-5 w-5" /></div>
        <div>
          <div className="font-display text-lg font-extrabold leading-none tracking-tight">TransLogiX</div>
          <div className="mt-1 max-w-[150px] truncate text-[11px] text-muted-foreground" data-testid="sidebar-company-name">{companyName || "Platform Console"}</div>
        </div>
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3">
        {NAV.filter((n) => n.roles.includes(role)).map((n) => n.children ? <NavGroup key={n.to} n={n} role={role} onNavigate={onNavigate} /> : (
          <NavLink
            key={n.to}
            to={n.to}
            onClick={onNavigate}
            data-testid={`nav-${n.to.slice(1)}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`
            }
          >
            <n.icon className="h-4 w-4" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t p-4">
        <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
        <div className="text-xs text-muted-foreground capitalize">{role?.replace("_", " ")}</div>
        <button data-testid="logout-btn" onClick={logout} className="mt-3 flex items-center gap-2 text-xs font-medium text-muted-foreground hover:text-destructive">
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
    </div>
  );
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  const { isSuper, actAs, setActAs, companyKey } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="flex min-h-screen bg-background">
      <aside className="no-print sticky top-0 hidden h-screen w-64 shrink-0 border-r bg-card lg:block"><SideNav /></aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-64 p-0 bg-card"><SideNav onNavigate={() => setOpen(false)} /></SheetContent>
      </Sheet>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="no-print sticky top-0 z-30 flex h-16 items-center gap-3 border-b bg-background/80 px-4 backdrop-blur-xl sm:px-6">
          <button data-testid="mobile-menu-btn" className="lg:hidden rounded-lg border p-2" onClick={() => setOpen(true)}><Menu className="h-4 w-4" /></button>
          {isSuper && actAs && (
            <div data-testid="acting-banner" className="flex min-w-0 items-center gap-2 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
              <span className="truncate">Managing: {actAs.name}</span>
              <button data-testid="exit-company-btn" onClick={() => { setActAs(null); navigate("/platform"); }} className="inline-flex items-center gap-1 font-semibold underline-offset-2 hover:underline">
                <ArrowLeftRight className="h-3 w-3" /> Exit
              </button>
            </div>
          )}
          <CompanySwitcher />
          <div className="flex-1" />
          <ThemeToggle />
        </header>
        <main className="mx-auto w-full max-w-[1400px] flex-1 p-4 sm:p-6 lg:p-8"><Outlet key={companyKey} /></main>
      </div>
    </div>
  );
}
